import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FcGoogle } from "react-icons/fc";
import { FaLinkedin } from "react-icons/fa";
import Logo from "../ui/Logo";
import {
  doesEmailExist,
  signUp,
  signIn,
} from "supertokens-web-js/recipe/emailpassword";
import { getAuthorisationURLWithQueryParamsAndSetState } from "supertokens-web-js/recipe/thirdparty";
import { Loader2 } from "lucide-react";
import AuthLayout from "@/features/auth/AuthLayout";

// Zod schemas for form validation
const signInSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(1, { message: "Please enter a valid password" }),
});

const signUpSchema = z.object({
  firstName: z.string().min(1, { message: "Please enter your first name" }),
  lastName: z.string().min(1, { message: "Please enter your last name" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" }),
});

export default function AuthPage({ isSignIn: initialIsSignIn = true }) {
  const [isSignIn, setIsSignIn] = useState(initialIsSignIn);
  const location = useLocation();
  const navigate = useNavigate(); // For React Router v6

  const params = new URLSearchParams(location.search);
  const redirectToPath = params.get("redirectToPath") || "/home";

  // React Hook Form for sign-in
  const signInForm = useForm({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onSubmit",
    shouldUnregister: true,
  });

  // React Hook Form for sign-up
  const signUpForm = useForm({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
    mode: "onSubmit",
    shouldUnregister: true,
  });

  // Function to toggle between sign-in and sign-up
  const toggleAuthMode = (isSignInMode) => {
    setIsSignIn(isSignInMode);
    if (isSignInMode) {
      signInForm.reset({
        email: "",
        password: "",
      });
    } else {
      signUpForm.reset({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
      });
    }
    navigate(isSignInMode ? "/auth/signin" : "/auth/signup");
  };

  async function googleSignInClicked() {
    try {
      const domain = `${import.meta.env.VITE_DOMAIN}/auth/callback/google`;
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: "google",

        // This is where Google should redirect the user back after login or error.
        // This URL goes on the Google's dashboard as well.
        frontendRedirectURI: domain,
      });

      window.location.assign(authUrl);
    } catch (err) {
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        window.alert(err.message);
      } else {
        window.alert("Oops! Something went wrong.");
      }
    }
  }

  async function linkedinSignInClicked() {
    try {
      const domain = `${import.meta.env.VITE_DOMAIN}/auth/callback/linkedin`;
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: "custom-linkedin",
        frontendRedirectURI: domain,
      });

      window.location.assign(authUrl);
    } catch (err) {
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        window.alert(err.message);
      } else {
        console.log(err);
        window.alert("Oops! Something went wrong.");
      }
    }
  }

  async function checkEmail(email) {
    try {
      const response = await doesEmailExist({ email });
      if (response.doesExist) {
        signUpForm.setError("email", {
          message: "Email already exists. Please sign in instead",
        });
        return false;
      }
      return true;
    } catch (err) {
      signUpForm.setError("email", { message: "Oops! Something went wrong." });
      return false;
    }
  }

  async function signInClicked(email, password) {
    try {
      const response = await signIn({
        formFields: [
          {
            id: "email",
            value: email,
          },
          {
            id: "password",
            value: password,
          },
        ],
      });

      if (response.status === "FIELD_ERROR") {
        response.formFields.forEach((formField) => {
          if (formField.id === "email") {
            signInForm.setError("email", { message: formField.error });
          } else if (formField.id === "password") {
            signInForm.setError("password", { message: formField.error });
          }
        });
      } else if (response.status === "WRONG_CREDENTIALS_ERROR") {
        signInForm.setError("password", {
          message: "Email password combination is incorrect",
        });
      } else if (response.status === "SIGN_IN_NOT_ALLOWED") {
        signInForm.setError("password", { message: response.reason });
      } else {
        navigate(redirectToPath, { replace: true });
      }
    } catch (err) {
      if (err.isSuperTokensGeneralError === true) {
        signInForm.setError("password", { message: err.message });
      } else {
        signInForm.setError("password", {
          message: "Oops! Something went wrong.",
        });
      }
    }
  }

  async function signUpClicked(email, password, firstName, lastName) {
    try {
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();

      const canProceed = await checkEmail(trimmedEmail);
      if (!canProceed) {
        return;
      }

      // Call the signup API with trimmed form fields
      const response = await signUp({
        formFields: [
          { id: "email", value: trimmedEmail },
          { id: "password", value: password },
          { id: "given_name", value: trimmedFirstName },
          { id: "family_name", value: trimmedLastName },
        ],
      });

      if (response.status === "FIELD_ERROR") {
        response.formFields.forEach(({ id, error }) => {
          if (error) {
            signUpForm.setError(id, { message: error });
          }
        });
      } else if (response.status === "SIGN_UP_NOT_ALLOWED") {
        signUpForm.setError("password", { message: response.reason });
      } else if (response.status === "OK") {
        window.location.href = "/verify-email";
      }
    } catch (err) {
      if (err.isSuperTokensGeneralError === true) {
        signUpForm.setError("email", { message: err.message });
      } else {
        signUpForm.setError("email", {
          message: "Email exists! Try logging in.",
        });
      }
    }
  }

  useEffect(() => {
    // Reset loading state when switching between sign-in and sign-up
  }, []);

  return (
    <AuthLayout>
      {/* Left side - Auth */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center space-x-3">
            <Logo className="w-8 h-8" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">
                Welcome to SamSearch
              </h2>
              <p className="text-sm sm:text-base  text-muted-foreground">
                Your AI Government Contracting Platform
              </p>
            </div>
          </div>
          <Card className="p-6">
            <div className="mb-4 flex justify-center space-x-4">
              <Button
                variant="ghost"
                className={isSignIn ? "text-primary" : "text-muted-foreground"}
                onClick={() => toggleAuthMode(true)} // Use toggleAuthMode
              >
                Sign In
              </Button>
              <Button
                variant="ghost"
                className={!isSignIn ? "text-primary" : "text-muted-foreground"}
                onClick={() => toggleAuthMode(false)} // Use toggleAuthMode
              >
                Sign Up
              </Button>
            </div>
            <div className="space-y-4">
              <Button
                variant="outline"
                onClick={googleSignInClicked}
                className="w-full justify-center space-x-2"
              >
                <FcGoogle className="h-5 w-5" />
                <span>{isSignIn ? "Sign in" : "Sign up"} with Google</span>
              </Button>
              <Button
                variant="outline"
                onClick={linkedinSignInClicked}
                className="w-full justify-center space-x-2"
              >
                <FaLinkedin color="#0077B5" className="h-5 w-5" />
                <span>{isSignIn ? "Sign in" : "Sign up"} with LinkedIn</span>
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>

                <div class="relative text-center text-xs after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                  <span class="relative z-10 bg-background px-2 text-muted-foreground uppercase">
                    Or continue with email
                  </span>
                </div>
              </div>

              {isSignIn ? (
                <Form {...signInForm} key="signin">
                  <form
                    onSubmit={signInForm.handleSubmit(async (data) => {
                      signInForm.clearErrors();
                      await signInClicked(data.email, data.password);
                    })}
                    className="space-y-4"
                  >
                    <FormField
                      control={signInForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="john@example.com"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signInForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={signInForm.formState.isSubmitting}
                    >
                      {signInForm.formState.isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin mr-2" /> Signing
                          in...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </form>
                  <a
                    href="/auth/reset-password"
                    className="text-blue-500 text-sm block"
                  >
                    Forgot Password?
                  </a>
                </Form>
              ) : (
                <Form {...signUpForm} key="signup">
                  <form
                    onSubmit={signUpForm.handleSubmit(async (data) => {
                      signUpForm.clearErrors();
                      await signUpClicked(
                        data.email,
                        data.password,
                        data.firstName,
                        data.lastName
                      );
                    })}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={signUpForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First name</FormLabel>
                            <FormControl>
                              <Input placeholder="John" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signUpForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last name</FormLabel>
                            <FormControl>
                              <Input placeholder="Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={signUpForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="john@example.com"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signUpForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={signUpForm.formState.isSubmitting}
                    >
                      {signUpForm.formState.isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin mr-2" /> Signing
                          up...
                        </>
                      ) : (
                        "Sign Up"
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AuthLayout>
  );
}
